import {
	parseWorkspaceItems,
	parseWorkspaceSearchHits,
} from "./schemas/workspace-context-schema";
import type { AgentChatReference } from "./types";

export type ResolveReferenceContextParams = {
	text: string;
	references?: AgentChatReference[];
	agentTools?: Record<string, unknown>;
};

const REFERENCE_LINK_REGEX = /#\[([^\]]+)\](?:\(([^)\s]+)\))?/g;
const REFERENCE_HASH_REGEX =
	/(?:^|\s)#([A-Za-z]{2,12}-[A-Za-z0-9_-]+|[A-Za-z][A-Za-z0-9_-]{2,})\b/g;

async function executeTool(
	tool: unknown,
	args: Record<string, unknown>,
): Promise<unknown> {
	if (
		tool &&
		typeof tool === "object" &&
		"execute" in tool &&
		typeof (tool as { execute: unknown }).execute === "function"
	) {
		const executor = tool as {
			execute: (args: unknown, options?: unknown) => Promise<unknown>;
		};
		return await executor.execute(args, {
			toolCallId: `reference_ctx_${Date.now()}`,
			messages: [],
		});
	}
	return null;
}

function escapeXml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&apos;");
}

function formatReferenceXml(item: {
	id: string;
	title: string;
	kind?: string;
	markdown?: string;
}): string {
	const attrs = [
		`id="${escapeXml(item.id)}"`,
		`title="${escapeXml(item.title)}"`,
	];
	if (item.kind) {
		attrs.push(`kind="${escapeXml(item.kind)}"`);
	}

	const body = item.markdown || `# ${item.title}`;
	return `<reference ${attrs.join(" ")}>\n${body}\n</reference>`;
}

export function extractReferenceTokensFromText(text: string): {
	ids: string[];
	titles: string[];
} {
	const ids: string[] = [];
	const titles: string[] = [];

	const linkMatches = text.matchAll(REFERENCE_LINK_REGEX);
	for (const match of linkMatches) {
		const label = match[1]?.trim();
		const id = match[2]?.trim();
		if (id && !ids.includes(id)) {
			ids.push(id);
		} else if (label && !titles.includes(label)) {
			titles.push(label);
		}
	}

	const hashMatches = text.matchAll(REFERENCE_HASH_REGEX);
	for (const match of hashMatches) {
		const token = match[1]?.trim();
		if (
			token &&
			(token.startsWith("Whiteboard-") ||
				token.startsWith("Card-") ||
				/^[A-Za-z]{2,5}-\d+$/i.test(token))
		) {
			if (!ids.includes(token)) {
				ids.push(token);
			}
		} else if (token && !titles.includes(token)) {
			titles.push(token);
		}
	}

	return { ids, titles };
}

export async function resolveReferenceContext({
	text,
	references = [],
	agentTools,
}: ResolveReferenceContextParams): Promise<string | null> {
	const getTool = agentTools?.["workspace.get"] ?? agentTools?.workspace_get;
	const searchTool =
		agentTools?.["workspace.search"] ?? agentTools?.workspace_search;

	const envelopes: string[] = [];
	const seenIds = new Set<string>();

	const directIds = references.map((r) => r.id).filter((id) => Boolean(id));

	const extracted = extractReferenceTokensFromText(text);
	for (const id of extracted.ids) {
		if (!directIds.includes(id)) {
			directIds.push(id);
		}
	}

	if (directIds.length > 0 && getTool) {
		try {
			const rawGet = await executeTool(getTool, { ids: directIds });
			const items = parseWorkspaceItems(rawGet);
			for (const item of items) {
				if (!seenIds.has(item.id)) {
					seenIds.add(item.id);
					envelopes.push(
						item.llmContent ||
							formatReferenceXml({
								id: item.id,
								title: item.name,
								kind: item.kind,
								markdown: item.markdown,
							}),
					);
				}
			}
		} catch (err) {
			console.warn("[resolveReferenceContext] workspace_get error:", err);
		}
	}

	// For any referenced item not loaded via getTool, output basic XML
	for (const ref of references) {
		if (!seenIds.has(ref.id)) {
			seenIds.add(ref.id);
			envelopes.push(
				formatReferenceXml({
					id: ref.id,
					title: ref.title,
				}),
			);
		}
	}

	// For extracted title tokens not in references, search via workspace.search
	const remainingTitles = extracted.titles.filter(
		(t) => !references.some((r) => r.title.toLowerCase() === t.toLowerCase()),
	);

	for (const title of remainingTitles) {
		if (searchTool && getTool) {
			try {
				const rawSearch = await executeTool(searchTool, {
					query: title,
					kinds: ["CARD", "WHITEBOARD"],
					limit: 3,
				});
				const hits = parseWorkspaceSearchHits(rawSearch);
				const hitIds = hits.map((h) => h.id).filter((id) => !seenIds.has(id));

				if (hitIds.length > 0) {
					const rawGet = await executeTool(getTool, { ids: hitIds });
					const items = parseWorkspaceItems(rawGet);
					for (const item of items) {
						if (!seenIds.has(item.id)) {
							seenIds.add(item.id);
							envelopes.push(
								item.llmContent ||
									formatReferenceXml({
										id: item.id,
										title: item.name,
										kind: item.kind,
										markdown: item.markdown,
									}),
							);
						}
					}
				}
			} catch (err) {
				console.warn("[resolveReferenceContext] workspace_search error:", err);
			}
		}
	}

	if (envelopes.length === 0) {
		return null;
	}

	return `<references>\n${envelopes.join("\n\n")}\n</references>`;
}
